import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormHelperText from "@mui/material/FormHelperText";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import { NewVehicleRow } from "../vehicles.types";
import { parseImportText } from "../parseImport";

function downloadTemplate() {
  const a = document.createElement("a");
  a.href = `${process.env.PUBLIC_URL}/fleet_template.xlsx`;
  a.download = "fleet_template.xlsx";
  a.click();
}

interface ImportSectionProps {
  onRowsParsed: (rows: NewVehicleRow[]) => void;
  disabled?: boolean;
}

export const ImportSection: React.FC<ImportSectionProps> = ({ onRowsParsed, disabled }) => {
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [pasteValue, setPasteValue] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processPaste = (text: string) => {
    try {
      const rows = parseImportText(text, "tsv");
      if (rows.length === 0) {
        setPasteError("No rows found. Check the column order: State, Plate Number, Make, Model, Type");
        return;
      }
      onRowsParsed(rows);
      setPasteValue("");
      setPasteError(null);
    } catch {
      setPasteError("Could not parse this content. Check the column order: State, Plate Number, Make, Model, Type");
    }
  };

  const handlePasteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPasteValue(e.target.value);
  };

  const handlePasteBlur = () => {
    if (pasteValue.trim()) {
      processPaste(pasteValue);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const text = e.clipboardData.getData("text");
    if (text.trim()) {
      e.preventDefault();
      processPaste(text);
    }
  };

  const processFile = (file: File) => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
    if (file.size > MAX_FILE_SIZE) {
      setFileError("File is too large. Please upload a file under 5 MB.");
      return;
    }

    const name = file.name.toLowerCase();

    if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
      const reader = new FileReader();
      reader.onerror = () => setFileError("Could not read the file. Please try again.");
      reader.onload = async (event) => {
        try {
          const data = event.target?.result;
          if (!data) {
            setFileError("The file appears to be empty");
            return;
          }
          const XLSX = await import("xlsx");
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const tsv = XLSX.utils.sheet_to_csv(sheet, { FS: "\t" });
          if (!tsv.trim()) {
            setFileError("The file appears to be empty");
            return;
          }
          const rows = parseImportText(tsv, "tsv");
          if (rows.length === 0) {
            setFileError(
              "Could not read this file. Check that it matches the expected column order: State, Plate Number, Make, Model, Type",
            );
            return;
          }
          onRowsParsed(rows);
          setFileError(null);
        } catch {
          setFileError(
            "Could not read this file. Check that it matches the expected column order: State, Plate Number, Make, Model, Type",
          );
        }
      };
      reader.readAsArrayBuffer(file);
      return;
    }

    let format: "csv" | "tsv";
    if (name.endsWith(".csv")) {
      format = "csv";
    } else if (name.endsWith(".tsv")) {
      format = "tsv";
    } else {
      setFileError("Please upload a .xlsx, .csv, or .tsv file");
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => setFileError("Could not read the file. Please try again.");
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (!text?.trim()) {
        setFileError("The file appears to be empty");
        return;
      }
      try {
        const rows = parseImportText(text, format);
        if (rows.length === 0) {
          setFileError(
            "Could not read this file. Check that it matches the expected column order: State, Plate Number, Make, Model, Type",
          );
          return;
        }
        onRowsParsed(rows);
        setFileError(null);
      } catch {
        setFileError(
          "Could not read this file. Check that it matches the expected column order: State, Plate Number, Make, Model, Type",
        );
      }
    };
    reader.readAsText(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    // Reset input so the same file can be re-uploaded
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Import Vehicles
      </Typography>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Column order: <strong>State, Plate Number, Make, Model, Type, Category (optional)</strong>
        </Typography>
        <Button size="small" startIcon={<DownloadIcon />} onClick={downloadTemplate} sx={{ flexShrink: 0, ml: 1 }}>
          Download Template
        </Button>
      </Stack>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {/* Paste area */}
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder="Paste rows from Excel here…"
            value={pasteValue}
            onChange={handlePasteChange}
            onBlur={handlePasteBlur}
            onPaste={handlePaste}
            disabled={disabled}
            error={!!pasteError}
            size="small"
          />
          {pasteError && <FormHelperText error>{pasteError}</FormHelperText>}
        </Box>

        {/* File upload */}
        <Box
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label="Upload vehicle file"
          sx={{
            flex: 1,
            minWidth: 200,
            border: "2px dashed",
            borderColor: isDragging ? "primary.main" : "divider",
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 1,
            cursor: disabled ? "not-allowed" : "pointer",
            bgcolor: isDragging ? "primary.50" : "background.paper",
            "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
          }}
          onDragOver={(e: React.DragEvent) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <UploadFileIcon color="action" sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary" align="center">
            Drop a .xlsx, .csv, or .tsv file here or click to upload
          </Typography>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv,.tsv" hidden onChange={handleFileChange} />
        </Box>
      </Box>
      {fileError && (
        <FormHelperText error sx={{ mt: 0.5 }}>
          {fileError}
        </FormHelperText>
      )}
    </Box>
  );
};

export default ImportSection;
